import { useState } from 'react';
import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import { Button, ButtonText, SearchInput } from '@morressier/alembic';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import { useQuery } from '@tanstack/react-query';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import {
  faEnvelope,
  faSearch,
  faPlus,
  faDeleteRight
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import EditCategoryModal from './EditCategoryModal';
import { listEmailTemplateCategories } from 'src/api/email-service';
import { TemplateCategoryDoc } from '@publishing-engine/domain/models/email-templates';
import EmailActionMenu from './EmailActionMenu';

type ColumnKey = keyof TemplateCategoryDoc;

// Define table column structure
interface TableColumn {
  key: ColumnKey;
  label: string;
  size: string | number;
}

const CATEGORIE_TABLE_COLUMNS: TableColumn[] = [
  {
    key: 'name',
    label: 'Email Tag',
    size: 2
  },
  {
    key: 'description',
    label: 'Description',
    size: 2
  }
];

const TableHeader = () => (
  <Thead>
    <Tr
      data-testId="categories-table-header"
      gap={2}
      sx={{
        '& th': {
          textTransform: 'none',
          color: 'black',
          background: 'white'
        }
      }}
    >
      {CATEGORIE_TABLE_COLUMNS.map(column => (
        <Th key={column.key} flex={column.size}>
          <Text textStyle="headingItem" whiteSpace="nowrap">
            {column.label}
          </Text>
        </Th>
      ))}
      <Th></Th>
    </Tr>
  </Thead>
);
export type IWorkspaceEmailCategoriesFilter = {
  searchParams: string;
};
const defaultValues: IWorkspaceEmailCategoriesFilter = {
  searchParams: ''
};
const EmailCategoriesTable = ({
  organizationId
}: {
  organizationId: string;
}) => {
  // State for managing UI page (1-based for display)
  const [uiPage, setUiPage] = useState<number>(1);
  // State for managing API page (0-based for backend)
  const [apiPage, setApiPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<TemplateCategoryDoc | null>(null);
  const [searchValue, setSearchValue] = useState('');

  // State for create modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Open edit modal for a template
  const handleEditTemplate = (category: TemplateCategoryDoc) => {
    setSelectedCategory(category);
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCategory(null);
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  // Close create modal
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };
  const { resetField, setValue, watch, reset } =
    useForm<IWorkspaceEmailCategoriesFilter>({
      defaultValues
    });
  const searchFilter = watch('searchParams');
  const {
    data: categoriesData,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: [
      'email-categories',
      { organizationId, apiPage, searchFilter, pageSize }
    ],
    queryFn: () =>
      organizationId
        ? listEmailTemplateCategories(organizationId, {
            skip: apiPage,
            limit: pageSize,
            search: searchFilter
          })
        : null
  });

  const categories = categoriesData?.data || [];

  // Total number of records across every page
  const totalItems = categoriesData?.total || 0;

  // Handle page change (convert from UI page to API page)
  const handlePageChange = (newPage: number) => {
    setUiPage(newPage);
    // API uses 0-based indexing and offset/skip instead of pages
    setApiPage((newPage - 1) * pageSize);
  };
  const offset = (uiPage - 1) * pageSize;

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, totalItems);

  return (
    <Flex direction="column" gap={4} width="100%" mt={3}>
      <Flex flexDirection={'column'} gap={4} width="100%">
        <Flex gap={4} flexWrap="wrap">
          <Flex
            gap={2}
            alignItems="center"
            flex={{ base: '1 1 100%', md: '1' }}
          >
            <SearchInput
              placeholder="Search by category"
              iconLeft={<FontAwesomeIcon icon={faSearch} />}
              width="100%"
              name="searchParams"
              value={searchValue}
              enterKeyHint="search"
              onChange={e => setSearchValue(e.currentTarget.value)}
              onInputClear={() => {
                setSearchValue('');
                resetField('searchParams');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setValue('searchParams', searchValue.trim());
                  setApiPage(0);
                }
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setValue('searchParams', searchValue.trim());
                  setApiPage(0);
                }
              }}
            />
            <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
              <Button
                variant="primary"
                aria-label="Search"
                onClick={() => {
                  setValue('searchParams', searchValue.trim());
                  setApiPage(0);
                }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </Box>
          </Flex>
          <Flex>
            <Button
              onClick={handleOpenCreateModal}
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
            >
              New Email Tag
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <Skeleton isLoaded={!isLoading}>
        <Flex alignItems={'center'} gap={2} minHeight={5} flexWrap="wrap">
          <Text textStyle="textBody">
            {totalItems > 0 ? (
              <>
                Showing <strong>{startIndex}</strong> –{' '}
                <strong>{endIndex}</strong> of <strong>{totalItems}</strong>{' '}
                email {totalItems === 1 ? 'tag' : 'tags'}
              </>
            ) : (
              <>
                <strong>0</strong> email tag
              </>
            )}
          </Text>
          {searchFilter && (
            <ButtonText
              onClick={() => {
                reset(defaultValues);
                setSearchValue('');
              }}
              leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
              variant="secondary"
            >
              Clear filters
            </ButtonText>
          )}
        </Flex>
      </Skeleton>
      {isLoading ? (
        <SubmissionLoader
          title="Loading Email Category"
          subTitle="We are retrieving your email category. This should only take a moment."
          bgColor="neutralSecondaryFill"
        />
      ) : isError ? (
        <SubmissionLoader
          title="Failed to Load Category"
          subTitle="There was an error loading the email category. Please try again later."
          bgColor="errorPrimaryFill"
        />
      ) : categories.length > 0 ? (
        <>
          <Box overflowX="auto" width="100%">
            <Table minWidth="500px">
              <TableHeader />
              <Tbody>
                {categories.map(
                  (category: TemplateCategoryDoc, index: number) => (
                    <Tr
                      key={index}
                      data-testId="category-table-row"
                      gap={2}
                      background="white"
                    >
                      {CATEGORIE_TABLE_COLUMNS.map(column =>
                        column.key === 'name' ? (
                          <Td
                            key={column.key}
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => handleEditTemplate(category)}
                          >
                            <Tooltip
                              hasArrow
                              borderRadius={4}
                              placement="top"
                              label={'Edit Email Category'}
                            >
                              <Text
                                textStyle="headingItem"
                                whiteSpace="normal"
                                textDecoration="none"
                                _hover={{
                                  textDecoration: 'underline',
                                  textDecorationColor: 'actionPrimaryFill'
                                }}
                                color="actionPrimaryFill"
                              >
                                {category[column.key]}
                              </Text>
                            </Tooltip>
                          </Td>
                        ) : (
                          <Td
                            key={column.key}
                            flex={column.size}
                            textAlign="left"
                          >
                            {category[column.key]}
                          </Td>
                        )
                      )}
                      <Td key="actions" width={20} textAlign="right">
                        <EmailActionMenu
                          category={category}
                          handleCategoryClick={() =>
                            handleEditTemplate(category)
                          }
                        />
                      </Td>
                    </Tr>
                  )
                )}
              </Tbody>
            </Table>
          </Box>
          <TablePagination
            ariaLabel="Email categories pagination"
            currentPage={uiPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : (
        <SubmissionLoader
          title="No Email Category Found"
          subTitle="There are no email category configured for this organization."
          icon={faEnvelope}
          bgColor="neutralSecondaryFill"
        />
      )}

      {/* Edit Template Modal */}
      {isEditModalOpen && (
        <EditCategoryModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          organizationId={organizationId}
          selectedCategory={selectedCategory}
          onSuccess={refetch}
          isCreating={false}
        />
      )}

      {/* Create Template Modal */}
      {isCreateModalOpen && (
        <EditCategoryModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          organizationId={organizationId}
          onSuccess={refetch}
          isCreating={true}
        />
      )}
    </Flex>
  );
};

export default EmailCategoriesTable;
